import type { OhlcData, Time } from "lightweight-charts";
import { isNullish } from "remeda";

import type { CandleData } from "../context";
import type { TFilterMinMax } from "../../../../data/live-charts/config";

function closestPrevCandleWithinRange(data: Partial<OhlcData<Time>>[], startingIdx: number, min: number, max: number) {
  for (let i = startingIdx - 1; i >= 0; i--) {
    const candle = data[i];
    const values = [candle.open, candle.close, candle.high, candle.low];
    const isOutsideRange = values.some((v) => (!isNullish(v) && v < min) || (!isNullish(v) && v > max));
    if (!isOutsideRange) {
      return candle;
    }
  }
  return null;
}

function closestNextCandleWithinRange(data: Partial<OhlcData<Time>>[], startingIdx: number, min: number, max: number) {
  for (let i = startingIdx; i < data.length; i++) {
    const candle = data[i];
    const values = [candle.open, candle.close, candle.high, candle.low];
    const isOutsideRange = values.some((v) => (!isNullish(v) && v < min) || (!isNullish(v) && v > max));
    if (!isOutsideRange) {
      return candle;
    }
  }
  return null;
}

export const filterMinMax = (state: TFilterMinMax | undefined, data: CandleData[]) => {
  if (!state?.enabled) return data;

  const min = state.min ?? Number.NEGATIVE_INFINITY;
  const max = state.max ?? Number.POSITIVE_INFINITY;
  const clamp = (val: number) => Math.max(min, Math.min(max, val));

  return data.map((d, idx) => {
    // Check if any of the values are outside the range
    const isOutsideRange = [d.open, d.close, d.high, d.low].some((v) => v < min || v > max);
    if (!isOutsideRange) return d;

    // The idea is to find the closest next and previous candles that are within the range, so the problematic candles can be replaced while keeping the candles connected.
    const nextCandle = closestNextCandleWithinRange(data, idx, min, max);
    const prevCandle = closestPrevCandleWithinRange(data, idx, min, max);

    // If both next and previous candles exist, set the values to the open of the next candle and close of the previous candle.
    if (nextCandle?.open && prevCandle?.close) {
      return {
        ...d,
        open: clamp(prevCandle.close),
        close: clamp(nextCandle.open),
        high: clamp(nextCandle.open),
        low: clamp(nextCandle.open),
        filtered: true,
        original: d,
      };
    }

    // If only the next candle exists, set the values to the open of the next candle.
    if (nextCandle?.open) {
      return {
        ...d,
        open: clamp(nextCandle.open),
        close: clamp(nextCandle.open),
        high: clamp(nextCandle.open),
        low: clamp(nextCandle.open),
        filtered: true,
        original: d,
      };
    }

    // If only the previous candle exists, set the values to the close of the previous candle.
    if (prevCandle?.close) {
      return {
        ...d,
        open: clamp(prevCandle.close),
        close: clamp(prevCandle.close),
        high: clamp(prevCandle.close),
        low: clamp(prevCandle.close),
        filtered: true,
        original: d,
      };
    }

    // Fallback: clamp the values to the min and max.
    return {
      ...d,
      open: clamp(d.open),
      close: clamp(d.close),
      high: clamp(d.high),
      low: clamp(d.low),
      filtered: true,
      original: d,
    };
  });
};
